import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { supabase } from '../../lib/supabase';

const MyTermsListDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [myTerms, setMyTerms] = useState([]);
  const [myDefinitions, setMyDefinitions] = useState([]);

  // 데이터 로드
  const loadData = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('로그인이 필요합니다.');
      }

      // 나만의 용어 로드
      const { data: termsData, error: termsError } = await supabase
        .from('my_terms')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (termsError) throw termsError;
      setMyTerms(termsData || []);

      // 나만의 정의 로드
      const { data: defsData, error: defsError } = await supabase
        .from('my_definitions')
        .select('*, terms:term_id(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (defsError) throw defsError;
      setMyDefinitions(defsData || []);

    } catch (error) {
      console.error('데이터 로드 실패:', error);
      enqueueSnackbar('데이터를 불러오는데 실패했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDelete = async (type, id) => {
    try {
      const table = type === 'term' ? 'my_terms' : 'my_definitions';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (error) throw error;

      enqueueSnackbar('삭제되었습니다.', { variant: 'success' });
      loadData();
    } catch (error) {
      console.error('삭제 실패:', error);
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 8
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          나만의 용어/정의 목록
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="나만의 용어" />
            <Tab label="나만의 정의" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 ? (
              <List>
                {myTerms.map((term) => (
                  <Accordion key={term.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {term.term}
                        </Typography>
                        <Chip
                          label={term.subject}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" gutterBottom>
                          {term.definition}
                        </Typography>
                        {term.korean_definition && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {term.korean_definition}
                          </Typography>
                        )}
                      </Box>
                      {term.keywords && term.keywords.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {term.keywords.map((keyword, idx) => (
                            <Chip
                              key={idx}
                              label={keyword}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete('term', term.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {myTerms.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    추가한 용어가 없습니다.
                  </Typography>
                )}
              </List>
            ) : (
              <List>
                {myDefinitions.map((def) => (
                  <Accordion key={def.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {def.terms?.term || '삭제된 용어'}
                        </Typography>
                        {def.terms?.subject && (
                          <Chip
                            label={def.terms.subject}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" gutterBottom>
                          {def.custom_definition}
                        </Typography>
                        {def.korean_definition && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {def.korean_definition}
                          </Typography>
                        )}
                      </Box>
                      {def.keywords && def.keywords.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {def.keywords.map((keyword, idx) => (
                            <Chip
                              key={idx}
                              label={keyword}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete('definition', def.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {myDefinitions.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    추가한 정의가 없습니다.
                  </Typography>
                )}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyTermsListDialog; 